import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Upload, Image, ChevronUp, ChevronDown, MapPin, Key, Bot, Loader2, CheckCircle, XCircle, BarChart2, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useTranslation } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_NETWORKS_BG = [
  { value: 'twitter', label: 'Twitter / X' },
  { value: 'viber', label: 'Viber' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'snapchat', label: 'Snapchat' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'reddit', label: 'Reddit' },
  { value: 'vk', label: 'VKontakte' },
  { value: 'threads', label: 'Threads' },
  { value: 'other', label: 'Друга' }
];

export default function AdminSettings() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    slides: [],
    slideshow_speed: 6,
    slideshow_order: 'sequential',
    slideshow_autoplay: true,
    slideshow_transition: 'fade',
    company_address: '',
    company_lat: '',
    company_lng: '',
    company_phone: '',
    company_email: '',
    sales_phone: '',
    sales_email: '',
    extra_phone_1: '',
    extra_email_1: '',
    extra_phone_2: '',
    extra_email_2: '',
    facebook_url: '',
    instagram_url: '',
    tiktok_url: '',
    linkedin_url: '',
    youtube_url: '',
    custom_socials: [],
    google_maps_api_key: '',
    gtm_id: '',
    ga4_id: '',
    meta_pixel_id: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const r = await axios.get(`${API}/site-settings`);
      if (r.data.hero_image && !r.data.slides?.length) {
        r.data.slides = [{
          id: '1',
          image: r.data.hero_image,
          title: r.data.hero_title || 'Модерни жилищни проекти в София',
          subtitle: r.data.hero_subtitle || '',
          cta_text: r.data.hero_cta_text || 'Разгледай проектите',
          cta_link: '/properties'
        }];
      }
      setSettings({
        slides: r.data.slides || [],
        slideshow_speed: r.data.slideshow_speed || 6,
        slideshow_order: r.data.slideshow_order || 'sequential',
        slideshow_autoplay: r.data.slideshow_autoplay !== false,
        slideshow_transition: r.data.slideshow_transition || 'fade',
        company_address: r.data.company_address || 'гр. София, п.к 1700, ж.к. Студентски град, ул. "8-ми Декември" №21',
        company_lat: r.data.company_lat || '42.6551',
        company_lng: r.data.company_lng || '23.3454',
        company_phone: r.data.company_phone || '+359 890 300 600',
        company_email: r.data.company_email || 'office@greencastle.bg',
        sales_phone: r.data.sales_phone || '+359 890 150 999',
        sales_email: r.data.sales_email || 'sales@greencastle.bg',
        extra_phone_1: r.data.extra_phone_1 || '',
        extra_email_1: r.data.extra_email_1 || '',
        extra_phone_2: r.data.extra_phone_2 || '',
        extra_email_2: r.data.extra_email_2 || '',
        facebook_url: r.data.facebook_url || 'https://www.facebook.com/GreenCastleBulgaria',
        instagram_url: r.data.instagram_url || '',
        tiktok_url: r.data.tiktok_url || '',
        linkedin_url: r.data.linkedin_url || '',
        youtube_url: r.data.youtube_url || '',
        custom_socials: r.data.custom_socials || [],
        google_maps_api_key: r.data.google_maps_api_key || '',
        gtm_id: r.data.gtm_id || '',
        ga4_id: r.data.ga4_id || '',
        meta_pixel_id: r.data.meta_pixel_id || ''
      });
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        ...settings,
        hero_image: settings.slides[0]?.image || '',
        hero_title: settings.slides[0]?.title || '',
        hero_subtitle: settings.slides[0]?.subtitle || '',
        hero_cta_text: settings.slides[0]?.cta_text || ''
      };
      await axios.put(`${API}/site-settings`, dataToSave);
      toast.success('Настройките са запазени');
    } catch (e) { toast.error(t('settings.save_error')); } finally { setSaving(false); }
  };

  const handleFileUpload = async (e, slideIndex) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const r = await axios.post(`${API}/upload?category=hero&entity_id=slide-${slideIndex}`, formData);
      const url = r.data.url?.startsWith('http') ? r.data.url : `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      const newSlides = [...settings.slides];
      newSlides[slideIndex].image = url;
      setSettings({ ...settings, slides: newSlides });
      toast.success('Изображението е качено');
    } catch (e) { toast.error('Грешка при качване'); }
  };

  const addSlide = () => {
    setSettings({
      ...settings,
      slides: [...settings.slides, {
        id: Date.now().toString(),
        image: '',
        title: 'Ново заглавие',
        subtitle: 'Описание на слайда',
        cta_text: 'Научи повече',
        cta_link: '/properties'
      }]
    });
  };

  const updateSlide = (index, field, value) => {
    const newSlides = [...settings.slides];
    newSlides[index][field] = value;
    setSettings({ ...settings, slides: newSlides });
  };

  const removeSlide = (index) => {
    if (settings.slides.length <= 1) {
      toast.error('Трябва да има поне един слайд');
      return;
    }
    setSettings({ ...settings, slides: settings.slides.filter((_, i) => i !== index) });
  };

  const moveSlide = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= settings.slides.length) return;
    const newSlides = [...settings.slides];
    [newSlides[index], newSlides[newIndex]] = [newSlides[newIndex], newSlides[index]];
    setSettings({ ...settings, slides: newSlides });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  return (
    <div data-testid="admin-settings">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-2">{t('settings.page_title')}</h1>
          <p className="text-muted-foreground">{t('settings.page_subtitle')}</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#1B5E37] hover:bg-[#154a2c]" data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? t('admin.saving') : t('settings.save_all')}
        </Button>
      </div>

      <Tabs defaultValue="slideshow" className="space-y-6">
        <TabsList className="bg-secondary">
          <TabsTrigger value="slideshow" className="data-[state=active]:bg-[#1B5E37] data-[state=active]:text-white">
            <Image className="w-4 h-4 mr-2" />{t('settings.tab.hero')}
          </TabsTrigger>
          <TabsTrigger value="contact" className="data-[state=active]:bg-[#1B5E37] data-[state=active]:text-white">
            <MapPin className="w-4 h-4 mr-2" />{t('settings.tab.contact')}
          </TabsTrigger>
          <TabsTrigger value="maps" className="data-[state=active]:bg-[#1B5E37] data-[state=active]:text-white" data-testid="maps-tab">
            <Key className="w-4 h-4 mr-2" />{t('settings.tab.maps')}
          </TabsTrigger>
          <TabsTrigger value="api" className="data-[state=active]:bg-[#1B5E37] data-[state=active]:text-white">
            <Key className="w-4 h-4 mr-1" />API Keys
          </TabsTrigger>
          <TabsTrigger value="ai" className="data-[state=active]:bg-[#1B5E37] data-[state=active]:text-white" data-testid="ai-tab">
            <Bot className="w-4 h-4 mr-2" />{t('settings.tab.ai')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="slideshow">
          <div className="admin-card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-heading font-semibold text-lg">Hero Slideshow</h3>
                <p className="text-sm text-muted-foreground">{t('settings.slideshow.subtitle')}</p>
              </div>
              <Button variant="outline" onClick={addSlide} data-testid="add-slide-btn"><Plus className="w-4 h-4 mr-2" />{t('settings.slideshow.add_slide')}</Button>
            </div>

            {/* Slideshow Settings */}
            <div className="mb-6 p-4 bg-secondary rounded-xl border border-border">
              <h4 className="font-medium text-sm mb-3">{t('settings.slideshow.settings')}</h4>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs">{t('settings.slideshow.speed')}</Label>
                  <Input 
                    type="number" min="2" max="30" step="1"
                    value={settings.slideshow_speed}
                    onChange={(e) => setSettings({ ...settings, slideshow_speed: parseInt(e.target.value) || 6 })}
                    className="mt-1"
                    data-testid="slideshow-speed-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('settings.slideshow.order')}</Label>
                  <Select value={settings.slideshow_order} onValueChange={(v) => setSettings({ ...settings, slideshow_order: v })}>
                    <SelectTrigger className="mt-1" data-testid="slideshow-order-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sequential">{t('settings.slideshow.sequential')}</SelectItem>
                      <SelectItem value="random">{t('settings.slideshow.random')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t('settings.slideshow.transition')}</Label>
                  <Select value={settings.slideshow_transition} onValueChange={(v) => setSettings({ ...settings, slideshow_transition: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fade">{t('settings.slideshow.fade')}</SelectItem>
                      <SelectItem value="slide">{t('settings.slideshow.slide')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <div className="flex items-center gap-3 p-2">
                    <button
                      onClick={() => setSettings({ ...settings, slideshow_autoplay: !settings.slideshow_autoplay })}
                      className={`w-10 h-5 rounded-full transition-colors ${settings.slideshow_autoplay ? 'bg-[#1B5E37]' : 'bg-gray-300'}`}
                      data-testid="slideshow-autoplay-toggle"
                    >
                      <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${settings.slideshow_autoplay ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                    <Label className="text-xs">{t('settings.slideshow.autoplay')}</Label>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {settings.slides.map((slide, index) => (
                <div key={slide.id} className="p-6 bg-secondary rounded-xl border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-medium text-sm bg-[#1B5E37] text-white px-3 py-1 rounded-full">
                      {t('settings.slideshow.slide')} {index + 1}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => moveSlide(index, -1)} disabled={index === 0}>
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => moveSlide(index, 1)} disabled={index === settings.slides.length - 1}>
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeSlide(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <Label className="mb-2 block">{t('settings.image')}</Label>
                      <div className="relative w-full h-48 rounded-lg overflow-hidden border border-border bg-muted">
                        {slide.image ? (
                          <img src={slide.image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                            <Image className="w-12 h-12" />
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Input 
                          value={slide.image} 
                          onChange={(e) => updateSlide(index, 'image', e.target.value)} 
                          placeholder={t('settings.image_url')} 
                          className="flex-1"
                        />
                        <label className="cursor-pointer">
                          <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, index)} />
                          <Button type="button" variant="outline" asChild><span><Upload className="w-4 h-4" /></span></Button>
                        </label>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label>{t('settings.title_label')}</Label>
                        <Input 
                          value={slide.title} 
                          onChange={(e) => updateSlide(index, 'title', e.target.value)} 
                          placeholder={t('settings.title_placeholder')}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>{t('settings.subtitle_label')}</Label>
                        <Textarea 
                          value={slide.subtitle} 
                          onChange={(e) => updateSlide(index, 'subtitle', e.target.value)} 
                          placeholder={t('settings.subtitle_placeholder')}
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>{t('settings.cta_text')}</Label>
                          <Input 
                            value={slide.cta_text} 
                            onChange={(e) => updateSlide(index, 'cta_text', e.target.value)} 
                            placeholder="Научи повече"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>{t('settings.cta_link')}</Label>
                          <Input 
                            value={slide.cta_link} 
                            onChange={(e) => updateSlide(index, 'cta_link', e.target.value)} 
                            placeholder="/properties"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {settings.slides.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('settings.no_slides')}</p>
                  <Button variant="outline" className="mt-4" onClick={addSlide}>
                    <Plus className="w-4 h-4 mr-2" />{t('settings.add_first_slide')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="contact">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="admin-card space-y-4">
              <h3 className="font-heading font-semibold text-lg">{t('settings.contact.title')}</h3>
              
              <div>
                <Label>{t('settings.contact.address')}</Label>
                <Input 
                  value={settings.company_address} 
                  onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
                  className="mt-1"
                  data-testid="company-address-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('settings.contact.phone_office')}</Label>
                  <Input 
                    value={settings.company_phone} 
                    onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{t('settings.contact.email_office')}</Label>
                  <Input 
                    value={settings.company_email} 
                    onChange={(e) => setSettings({ ...settings, company_email: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('settings.contact.phone_sales')}</Label>
                  <Input 
                    value={settings.sales_phone} 
                    onChange={(e) => setSettings({ ...settings, sales_phone: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{t('settings.contact.email_sales')}</Label>
                  <Input 
                    value={settings.sales_email} 
                    onChange={(e) => setSettings({ ...settings, sales_email: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('settings.contact.extra_phone_1')}</Label>
                  <Input 
                    value={settings.extra_phone_1} 
                    onChange={(e) => setSettings({ ...settings, extra_phone_1: e.target.value })}
                    placeholder={t('settings.contact.optional')}
                    className="mt-1"
                    data-testid="extra-phone-1-input"
                  />
                </div>
                <div>
                  <Label>{t('settings.contact.extra_email_1')}</Label>
                  <Input 
                    value={settings.extra_email_1} 
                    onChange={(e) => setSettings({ ...settings, extra_email_1: e.target.value })}
                    placeholder="Оставете празно ако не е нужно"
                    className="mt-1"
                    data-testid="extra-email-1-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('settings.contact.extra_phone_2')}</Label>
                  <Input 
                    value={settings.extra_phone_2} 
                    onChange={(e) => setSettings({ ...settings, extra_phone_2: e.target.value })}
                    placeholder="Оставете празно ако не е нужно"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{t('settings.contact.extra_email_2')}</Label>
                  <Input 
                    value={settings.extra_email_2} 
                    onChange={(e) => setSettings({ ...settings, extra_email_2: e.target.value })}
                    placeholder="Оставете празно ако не е нужно"
                    className="mt-1"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Допълнителните телефони и имейли се показват в контактната секция само ако са попълнени.</p>
              <div>
                <Label>Facebook</Label>
                <Input 
                  value={settings.facebook_url} 
                  onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })}
                  placeholder="https://www.facebook.com/..."
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Instagram</Label>
                  <Input 
                    value={settings.instagram_url} 
                    onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })}
                    placeholder="https://www.instagram.com/..."
                    className="mt-1"
                    data-testid="instagram-url-input"
                  />
                </div>
                <div>
                  <Label>YouTube</Label>
                  <Input 
                    value={settings.youtube_url} 
                    onChange={(e) => setSettings({ ...settings, youtube_url: e.target.value })}
                    placeholder="https://www.youtube.com/@..."
                    className="mt-1"
                    data-testid="youtube-url-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>TikTok</Label>
                  <Input 
                    value={settings.tiktok_url} 
                    onChange={(e) => setSettings({ ...settings, tiktok_url: e.target.value })}
                    placeholder="https://www.tiktok.com/@..."
                    className="mt-1"
                    data-testid="tiktok-url-input"
                  />
                </div>
                <div>
                  <Label>LinkedIn</Label>
                  <Input 
                    value={settings.linkedin_url} 
                    onChange={(e) => setSettings({ ...settings, linkedin_url: e.target.value })}
                    placeholder="https://www.linkedin.com/company/..."
                    className="mt-1"
                    data-testid="linkedin-url-input"
                  />
                </div>
              </div>

              {/* Custom Social Networks */}
              <div className="pt-2 border-t border-border">
                <Label className="mb-2 block font-medium">{t('settings.contact.extra_socials')}</Label>
                <p className="text-xs text-muted-foreground mb-3">Добавете други социални мрежи от падащото меню</p>
                
                {settings.custom_socials.map((cs, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <Select 
                      value={cs.platform} 
                      onValueChange={(v) => {
                        const updated = [...settings.custom_socials];
                        updated[idx].platform = v;
                        setSettings({ ...settings, custom_socials: updated });
                      }}
                    >
                      <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SOCIAL_NETWORKS_BG.map(sn => (
                          <SelectItem key={sn.value} value={sn.value}>{sn.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input 
                      value={cs.url}
                      onChange={(e) => {
                        const updated = [...settings.custom_socials];
                        updated[idx].url = e.target.value;
                        setSettings({ ...settings, custom_socials: updated });
                      }}
                      placeholder="https://..."
                      className="flex-1"
                    />
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => {
                      setSettings({ ...settings, custom_socials: settings.custom_socials.filter((_, i) => i !== idx) });
                    }}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => {
                  setSettings({ ...settings, custom_socials: [...settings.custom_socials, { platform: 'twitter', url: '' }] });
                }} data-testid="add-custom-social-btn">
                  <Plus className="w-4 h-4 mr-1" />{t('settings.contact.add_network')}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground">Иконките се показват в хедъра и футъра само когато URL полето е попълнено.</p>
            </div>

            <div className="admin-card space-y-4">
              <h3 className="font-heading font-semibold text-lg">{t('settings.location.title')}</h3>
              <p className="text-sm text-muted-foreground">{t('settings.location.subtitle')}</p>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('settings.location.lat')}</Label>
                  <Input 
                    type="number"
                    step="any"
                    value={settings.company_lat} 
                    onChange={(e) => setSettings({ ...settings, company_lat: e.target.value })}
                    placeholder="42.6551"
                    className="mt-1"
                    data-testid="company-lat-input"
                  />
                </div>
                <div>
                  <Label>{t('settings.location.lng')}</Label>
                  <Input 
                    type="number"
                    step="any"
                    value={settings.company_lng} 
                    onChange={(e) => setSettings({ ...settings, company_lng: e.target.value })}
                    placeholder="23.3454"
                    className="mt-1"
                    data-testid="company-lng-input"
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label className="mb-2 block">{t('settings.location.map_preview')}</Label>
                <div className="w-full h-48 rounded-lg overflow-hidden border border-border">
                  <iframe 
                    src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2933.7!2d${settings.company_lng}!3d${settings.company_lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDLCsDM5JzE4LjQiTiAyM8KwMjAnNDMuNCJF!5e0!3m2!1sbg!2sbg!4v1704067200000!5m2!1sbg!2sbg`}
                    width="100%" 
                    height="100%" 
                    style={{ border: 0 }} 
                    allowFullScreen="" 
                    loading="lazy"
                    title="Location Preview"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Съвет: Намерете координатите от Google Maps - кликнете с десен бутон върху локацията
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="maps">
          <div className="admin-card max-w-2xl">
            <h3 className="font-heading font-semibold text-lg mb-2">{t('settings.maps.title')}</h3>
            <p className="text-sm text-muted-foreground mb-6">
              За да работи интерактивната карта на сайта, е необходим Google Maps API ключ. 
              Можете да го получите от{' '}
              <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-[#1B5E37] underline">
                Google Cloud Console
              </a>. Активирайте "Maps JavaScript API" и "Geocoding API".
            </p>
            
            <div className="space-y-4">
              <div>
                <Label>{t('settings.maps.api_key_label')}</Label>
                <Input 
                  type="password"
                  value={settings.google_maps_api_key} 
                  onChange={(e) => setSettings({ ...settings, google_maps_api_key: e.target.value })}
                  placeholder="AIzaSy..."
                  className="mt-1 font-mono"
                  data-testid="google-maps-api-key-input"
                />
              </div>
              
              <div className="p-4 bg-secondary rounded-lg border border-border">
                <h4 className="font-medium text-sm mb-2">Как да получите Google Maps API ключ:</h4>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Отидете на <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-[#1B5E37] underline">Google Cloud Console</a></li>
                  <li>Създайте проект (или изберете съществуващ)</li>
                  <li>Отидете на APIs & Services &gt; Library</li>
                  <li>Активирайте "Maps JavaScript API"</li>
                  <li>Отидете на APIs & Services &gt; Credentials</li>
                  <li>Създайте API Key</li>
                  <li>Поставете ключа по-горе и натиснете "Запази"</li>
                </ol>
              </div>

              {settings.google_maps_api_key && (
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-4 py-2 rounded-lg">
                  <Key className="w-4 h-4" />
                  <span>API ключът е конфигуриран. Интерактивната карта е активна.</span>
                </div>
              )}

              {!settings.google_maps_api_key && (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-4 py-2 rounded-lg">
                  <Key className="w-4 h-4" />
                  <span>Без API ключ картата ще показва вградена Google Maps рамка (iframe).</span>
                </div>
              )}
            </div>

            {/* Tracking & Analytics */}
            <div className="bg-card rounded-xl p-6 border border-border">
              <h3 className="font-heading font-semibold text-lg mb-1 flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-[#1B5E37]" />
                Tracking & Анализ
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Google Tag Manager, GA4 и Meta Pixel за проследяване на посетителите
              </p>
              <div className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2 mb-1.5">
                    Google Tag Manager ID
                    <span className="text-xs text-muted-foreground font-normal">(GTM-XXXXXXX)</span>
                  </Label>
                  <Input
                    placeholder="GTM-XXXXXXX"
                    value={settings.gtm_id}
                    onChange={e => setSettings(s => ({ ...s, gtm_id: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Препоръчително — управлява всички tracking скриптове</p>
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-1.5">
                    Google Analytics 4 ID
                    <span className="text-xs text-muted-foreground font-normal">(G-XXXXXXXXXX)</span>
                  </Label>
                  <Input
                    placeholder="G-XXXXXXXXXX"
                    value={settings.ga4_id}
                    onChange={e => setSettings(s => ({ ...s, ga4_id: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-1.5">
                    Meta Pixel ID
                    <span className="text-xs text-muted-foreground font-normal">(Facebook/Instagram реклами)</span>
                  </Label>
                  <Input
                    placeholder="XXXXXXXXXXXXXXXXXX"
                    value={settings.meta_pixel_id}
                    onChange={e => setSettings(s => ({ ...s, meta_pixel_id: e.target.value }))}
                  />
                </div>
                {(settings.gtm_id || settings.ga4_id || settings.meta_pixel_id) && (
                  <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-4 py-2 rounded-lg">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Tracking е конфигуриран. Запази настройките за да се активира.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="api">
          <ApiKeysPanel />
        </TabsContent>

        {/* AI Integration Tab */}
        <TabsContent value="ai">
          <AiSettingsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}


// AI Settings Panel Component
function AiSettingsPanel() {
  const [aiSettings, setAiSettings] = useState({
    provider: '',
    api_key: '',
    model: '',
    custom_url: '',
    enabled: false
  });
  const [providers, setProviders] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/ai/providers`),
      axios.get(`${API}/ai/settings`)
    ]).then(([provRes, settRes]) => {
      setProviders(provRes.data);
      const s = settRes.data;
      setAiSettings({
        provider: s.provider || '',
        api_key: s.api_key_masked || s.api_key || '',
        model: s.model || '',
        custom_url: s.custom_url || '',
        enabled: s.enabled || false
      });
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ai/settings`, aiSettings);
      toast.success('AI настройките са запазени');
      // Refresh to get masked key
      const r = await axios.get(`${API}/ai/settings`);
      setAiSettings(prev => ({
        ...prev,
        api_key: r.data.api_key_masked || prev.api_key
      }));
    } catch (e) {
      toast.error('Грешка при запазване');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API}/ai/test`, {
        provider: aiSettings.provider,
        api_key: aiSettings.api_key,
        model: aiSettings.model,
        custom_url: aiSettings.custom_url
      });
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ success: false, message: e.response?.data?.detail || 'Грешка при тест' });
    } finally { setTesting(false); }
  };

  const currentModels = providers[aiSettings.provider]?.models || [];

  if (loading) return <div className="flex items-center justify-center h-32"><div className="spinner"></div></div>;

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
        <strong>AI Асистент</strong> — Claude API за генериране и подобряване на текст директно в редактора на проекти и обяви.
        За external интеграции (AI агенти, n8n, Telegram бот) използвай таба <strong>API Keys</strong>.
      </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Provider Settings */}
      <div className="admin-card space-y-5">
        <div>
          <h3 className="font-heading font-semibold text-lg mb-1">AI Доставчик</h3>
          <p className="text-sm text-muted-foreground">Изберете AI доставчик и въведете API ключ</p>
        </div>

        {/* Enable toggle */}
        <div className="flex items-center justify-between p-4 bg-secondary rounded-lg border border-border">
          <div>
            <Label className="font-medium">Активирай AI интеграцията</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Позволява използването на AI функции в админ панела</p>
          </div>
          <button
            onClick={() => setAiSettings(s => ({ ...s, enabled: !s.enabled }))}
            className={`w-12 h-6 rounded-full transition-colors ${aiSettings.enabled ? 'bg-[#1B5E37]' : 'bg-gray-300'}`}
            data-testid="ai-enabled-toggle"
          >
            <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${aiSettings.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
          </button>
        </div>

        {/* Provider Selection */}
        <div>
          <Label>AI Доставчик</Label>
          <Select value={aiSettings.provider} onValueChange={(v) => setAiSettings(s => ({ ...s, provider: v, model: '', api_key: '' }))}>
            <SelectTrigger className="mt-1" data-testid="ai-provider-select">
              <SelectValue placeholder="Изберете доставчик..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="openai">OpenAI (GPT-4o, GPT-4o-mini)</SelectItem>
              <SelectItem value="anthropic">Anthropic (Claude Sonnet, Haiku)</SelectItem>
              <SelectItem value="gemini">Google Gemini (2.0 Flash, 1.5 Pro)</SelectItem>
              <SelectItem value="custom">Custom (OpenAI-съвместим)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* API Key */}
        <div>
          <Label>API Ключ</Label>
          <Input 
            type="password"
            value={aiSettings.api_key}
            onChange={(e) => setAiSettings(s => ({ ...s, api_key: e.target.value }))}
            placeholder={aiSettings.provider === 'openai' ? 'sk-...' : aiSettings.provider === 'anthropic' ? 'sk-ant-...' : 'Въведете API ключ'}
            className="mt-1 font-mono"
            data-testid="ai-api-key-input"
          />
          <p className="text-xs text-muted-foreground mt-1">
            {aiSettings.provider === 'openai' && 'Получете ключ от platform.openai.com/api-keys'}
            {aiSettings.provider === 'anthropic' && 'Получете ключ от console.anthropic.com'}
            {aiSettings.provider === 'gemini' && 'Получете ключ от aistudio.google.com/apikey'}
            {aiSettings.provider === 'custom' && 'Въведете API ключ за вашия OpenAI-съвместим сървър'}
          </p>
        </div>

        {/* Model Selection */}
        <div>
          <Label>Модел</Label>
          {currentModels.length > 0 ? (
            <Select value={aiSettings.model} onValueChange={(v) => setAiSettings(s => ({ ...s, model: v }))}>
              <SelectTrigger className="mt-1" data-testid="ai-model-select">
                <SelectValue placeholder="Изберете модел..." />
              </SelectTrigger>
              <SelectContent>
                {currentModels.map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input 
              value={aiSettings.model}
              onChange={(e) => setAiSettings(s => ({ ...s, model: e.target.value }))}
              placeholder="напр. gpt-4o-mini"
              className="mt-1"
              data-testid="ai-model-input"
            />
          )}
        </div>

        {/* Custom URL */}
        {(aiSettings.provider === 'custom' || aiSettings.custom_url) && (
          <div>
            <Label>Custom API URL</Label>
            <Input 
              value={aiSettings.custom_url}
              onChange={(e) => setAiSettings(s => ({ ...s, custom_url: e.target.value }))}
              placeholder="https://your-api.com/v1/chat/completions"
              className="mt-1 font-mono text-sm"
              data-testid="ai-custom-url-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Трябва да е съвместим с OpenAI Chat Completions API формата
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handleSave} disabled={saving} className="bg-[#1B5E37] hover:bg-[#154a2c]" data-testid="save-ai-settings-btn">
            <Save className="w-4 h-4 mr-2" />{saving ? 'Запазване...' : 'Запази AI настройки'}
          </Button>
          <Button 
            variant="outline" 
            onClick={handleTest} 
            disabled={testing || !aiSettings.provider || !aiSettings.api_key}
            data-testid="test-ai-btn"
          >
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
            Тест на връзката
          </Button>
        </div>

        {/* Test Result */}
        {testResult && (
          <div className={`flex items-start gap-2 p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid="ai-test-result">
            {testResult.success ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
            <div>
              <p className={`font-medium text-sm ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {testResult.success ? 'Връзката е успешна!' : 'Грешка при свързване'}
              </p>
              <p className="text-xs mt-1 text-muted-foreground">{testResult.message}</p>
            </div>
          </div>
        )}
      </div>

      {/* Info Panel */}
      <div className="space-y-6">
        <div className="admin-card">
          <h3 className="font-heading font-semibold text-lg mb-3">Как работи AI интеграцията</h3>
          <div className="space-y-4 text-sm text-muted-foreground">
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-[#1B5E37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-[#1B5E37]">1</span>
              </div>
              <div>
                <p className="font-medium text-foreground">Изберете доставчик</p>
                <p>OpenAI, Anthropic (Claude), Google Gemini или свой сървър</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-[#1B5E37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-[#1B5E37]">2</span>
              </div>
              <div>
                <p className="font-medium text-foreground">Въведете API ключ</p>
                <p>Ключът се пази сигурно на сървъра и не се показва на клиента</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-[#1B5E37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-[#1B5E37]">3</span>
              </div>
              <div>
                <p className="font-medium text-foreground">Използвайте AI в панела</p>
                <p>Генерирайте текстове, подобрявайте описания, чатете с AI асистента</p>
              </div>
            </div>
          </div>
        </div>

        <div className="admin-card">
          <h3 className="font-heading font-semibold text-base mb-3">AI Функции</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-[#1B5E37] flex-shrink-0 mt-0.5" />
              <span><strong>Генериране на описания</strong> — създава професионални описания на имоти</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-[#1B5E37] flex-shrink-0 mt-0.5" />
              <span><strong>Подобряване на текст</strong> — прави текстове по-привлекателни</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-[#1B5E37] flex-shrink-0 mt-0.5" />
              <span><strong>AI Чат</strong> — интерактивен асистент за въпроси и съвети</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-[#1B5E37] flex-shrink-0 mt-0.5" />
              <span><strong>BYOK</strong> — Вашият ключ, вашият контрол над разходите</span>
            </li>
          </ul>
        </div>

        {/* Provider links */}
        <div className="admin-card">
          <h3 className="font-heading font-semibold text-base mb-3">Полезни линкове</h3>
          <div className="space-y-2">
            <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#1B5E37] hover:underline">
              OpenAI — Получете API ключ
            </a>
            <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#1B5E37] hover:underline">
              Anthropic — Claude API Console
            </a>
            <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#1B5E37] hover:underline">
              Google — Gemini API Key
            </a>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}

// API Keys Panel
function ApiKeysPanel() {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDesc, setNewKeyDesc] = useState('');
  const [newKeyPerms, setNewKeyPerms] = useState('admin');
  const [createdKey, setCreatedKey] = useState(null);

  useEffect(() => { fetchKeys(); }, []);

  const fetchKeys = async () => {
    try {
      const r = await axios.get(`${API}/api-keys`);
      setKeys(r.data || []);
    } catch {} finally { setLoading(false); }
  };

  const createKey = async () => {
    if (!newKeyName.trim()) return;
    try {
      const r = await axios.post(`${API}/api-keys`, {
        name: newKeyName,
        description: newKeyDesc,
        permissions: newKeyPerms === 'admin' ? ['admin', 'write', 'read'] : newKeyPerms === 'write' ? ['write', 'read'] : ['read']
      });
      setCreatedKey(r.data.key);
      setNewKeyName(''); setNewKeyDesc('');
      setCreating(false);
      fetchKeys();
      toast.success('API Key създаден');
    } catch { toast.error('Грешка'); }
  };

  const revokeKey = async (id) => {
    if (!window.confirm('Отмени API Key?')) return;
    try {
      await axios.delete(`${API}/api-keys/${id}`);
      toast.success('API Key е отменен');
      fetchKeys();
    } catch { toast.error('Грешка'); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
              <Key className="w-5 h-5 text-[#1B5E37]" /> API Keys
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              За интеграция с AI агенти, n8n, Make.com или друг external инструмент
            </p>
          </div>
          <Button onClick={() => setCreating(true)} className="bg-[#1B5E37] hover:bg-[#154a2c]" size="sm">
            <Plus className="w-4 h-4 mr-1" /> Нов Key
          </Button>
        </div>

        {/* Info box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4 text-sm text-blue-800">
          <strong>Как да използвате:</strong> Добавете header <code className="bg-blue-100 px-1 rounded">x-api-key: gc_...</code> към всяка заявка към API-то.
          <br />Endpoint: <code className="bg-blue-100 px-1 rounded">https://greencastle-bg.onrender.com/api/</code>
        </div>

        {/* Created key display */}
        {createdKey && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm font-medium text-green-800 mb-2">⚠️ Запазете ключа — показва се само веднъж!</p>
            <code className="text-xs bg-white border px-3 py-2 rounded block break-all">{createdKey}</code>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => { navigator.clipboard.writeText(createdKey); toast.success('Копиран!'); }}>
              Копирай
            </Button>
            <Button size="sm" variant="ghost" className="mt-2 ml-2" onClick={() => setCreatedKey(null)}>Затвори</Button>
          </div>
        )}

        {/* Create form */}
        {creating && (
          <div className="mt-4 p-4 border border-border rounded-lg space-y-3">
            <Input placeholder="Име (напр. AI Agent, n8n)" value={newKeyName} onChange={e => setNewKeyName(e.target.value)} />
            <Input placeholder="Описание (по желание)" value={newKeyDesc} onChange={e => setNewKeyDesc(e.target.value)} />
            <select value={newKeyPerms} onChange={e => setNewKeyPerms(e.target.value)}
              className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background">
              <option value="admin">Пълен достъп (Admin)</option>
              <option value="write">Четене + Писане (Manager)</option>
              <option value="read">Само четене</option>
            </select>
            <div className="flex gap-2">
              <Button onClick={createKey} className="bg-[#1B5E37] hover:bg-[#154a2c]" disabled={!newKeyName.trim()}>Създай</Button>
              <Button variant="outline" onClick={() => setCreating(false)}>Отказ</Button>
            </div>
          </div>
        )}

        {/* Keys list */}
        <div className="mt-4 space-y-2">
          {loading ? <p className="text-sm text-muted-foreground">Зареждане...</p> :
           keys.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">Няма създадени API Keys</p> :
           keys.map(k => (
            <div key={k.id} className="flex items-center justify-between p-3 border border-border rounded-lg bg-secondary/20">
              <div>
                <p className="font-medium text-sm">{k.name}</p>
                <p className="text-xs text-muted-foreground">
                  {k.description && <span className="mr-2">{k.description}</span>}
                  Създаден: {new Date(k.created_at).toLocaleDateString('bg-BG')}
                  {k.last_used_at && <span className="ml-2">• Използван: {new Date(k.last_used_at).toLocaleDateString('bg-BG')}</span>}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${k.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {k.is_active ? 'Активен' : 'Отменен'}
                </span>
                {k.is_active && (
                  <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => revokeKey(k.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
